import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import AiPromptGenerator from "@/components/resume-builder/AiPromptGenerator";

const FIELD_CLASS = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400";
const EMPTY_ENTRY = { title: "", company: "", start_date: "", end_date: "", current: false, bullets: [] };

function InternshipEntryCard({ entry, idx, onChange, onRemove }) {
  const [suggesting, setSuggesting] = useState(false);

  const update = (field, value) => onChange(idx, { ...entry, [field]: value });

  const suggestBullets = async () => {
    if (!entry.title.trim()) {
      toast.error("Add an internship title first");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/resume-builder/suggest-bullets", { job_title: entry.title, company: entry.company, context: "This was an internship." });
      update("bullets", [...entry.bullets, ...data.bullets]);
    } catch (e) {
      toast.error("Could not suggest bullets right now");
    } finally {
      setSuggesting(false);
    }
  };

  const updateBullet = (i, value) => update("bullets", entry.bullets.map((b, bi) => (bi === i ? value : b)));
  const removeBullet = (i) => update("bullets", entry.bullets.filter((_, bi) => bi !== i));

  return (
    <div className="bg-white/[0.03] rounded-xl p-4" data-testid={`resume-internship-entry-${idx}`}>
      <div className="flex justify-end mb-2">
        <button onClick={() => onRemove(idx)} data-testid={`resume-remove-internship-${idx}`} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={entry.title} onChange={(e) => update("title", e.target.value)} placeholder="Internship Title" data-testid={`resume-internship-title-${idx}`} className={FIELD_CLASS} />
        <input value={entry.company} onChange={(e) => update("company", e.target.value)} placeholder="Company" data-testid={`resume-internship-company-${idx}`} className={FIELD_CLASS} />
        <div className="flex gap-2 sm:col-span-2">
          <input value={entry.start_date} onChange={(e) => update("start_date", e.target.value)} placeholder="Start" data-testid={`resume-internship-start-${idx}`} className={FIELD_CLASS + " flex-1"} />
          <input value={entry.end_date} onChange={(e) => update("end_date", e.target.value)} placeholder="End" disabled={entry.current} data-testid={`resume-internship-end-${idx}`} className={FIELD_CLASS + " flex-1 disabled:opacity-50"} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400 mb-3">
        <input type="checkbox" checked={entry.current} onChange={(e) => update("current", e.target.checked)} data-testid={`resume-internship-current-${idx}`} /> I'm currently interning here
      </label>

      <div className="space-y-1.5 mb-2">
        {entry.bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={b} onChange={(e) => updateBullet(i, e.target.value)} data-testid={`resume-internship-bullet-${idx}-${i}`} className={FIELD_CLASS + " flex-1"} />
            <button onClick={() => removeBullet(i)} data-testid={`resume-remove-internship-bullet-${idx}-${i}`} className="text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button onClick={suggestBullets} disabled={suggesting} data-testid={`resume-suggest-internship-bullets-${idx}`} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-60">
        {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Suggest Bullets
      </button>
    </div>
  );
}

export default function InternshipsSection({ draft, setDraft }) {
  const updateEntry = (idx, entry) => setDraft({ ...draft, internships: draft.internships.map((e, i) => (i === idx ? entry : e)) });
  const removeEntry = (idx) => setDraft({ ...draft, internships: draft.internships.filter((_, i) => i !== idx) });
  const addEntry = () => setDraft({ ...draft, internships: [...draft.internships, { ...EMPTY_ENTRY }] });

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="resume-section-internships">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-white flex items-center gap-1.5">Internships <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-indigo-300 bg-indigo-400/10 border border-indigo-400/25 rounded-full px-1.5 py-0.5"><Sparkles className="w-2.5 h-2.5" /> AI powered</span></p>
        <button onClick={addEntry} data-testid="resume-add-internship-button" className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
          <Plus className="w-3.5 h-3.5" /> Add Internship
        </button>
      </div>
      <div className="space-y-3">
        {draft.internships.map((entry, idx) => (
          <InternshipEntryCard key={idx} entry={entry} idx={idx} onChange={updateEntry} onRemove={removeEntry} />
        ))}
        {draft.internships.length === 0 && <p className="text-xs text-slate-400">No internships added yet.</p>}
      </div>
      <div className="mt-3">
        <AiPromptGenerator section="internships" draft={draft} setDraft={setDraft} />
      </div>
    </div>
  );
}
